import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { MOBILE_BREAKPOINT } from "./breakpoints";

const StyledFooter = styled.div({
    position: "fixed",
    bottom: 0,
    left: tokens.globals["spacing-20"],
    width: `calc(100% - ${tokens.globals["spacing-20"]})`,
    padding: "20px 30px",
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(7px)",
    boxShadow: tokens.effects.effects.shadow.default,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-5"],
    zIndex: 1000,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        left: 0,
        width: "100%",
        padding: `${tokens.globals["spacing-3"]} ${tokens.globals["spacing-md"]}`,
        flexWrap: "wrap",
    },
});

const buttonBase = {
    height: "35px",
    padding: `${tokens.globals["spacing-sm"]} ${tokens.globals["spacing-md"]}`,
    borderRadius: tokens.globals["border-radius"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: tokens.typography.lineHeights["110"],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: "pointer",
    transition: "all 0.15s ease-in-out",
};

const StyledSaveButton = styled.button(() => ({
    ...buttonBase,
    background: tokens.colors.theme["button-color"],
    boxShadow: tokens.effects.effects.shadow.default,
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.theme["button-color"]}`,
    color: tokens.colors.neutral.white,

    "&:hover:not(:disabled)": {
        background: tokens.colors.theme["button-hover-color"],
        borderColor: tokens.colors.theme["button-hover-color"],
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.theme["button-color"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
}));

const StyledCancelButton = styled.button(({ $tone = "destructive" }) => {
    const destructive = $tone === "destructive";
    return {
        ...buttonBase,
        background: destructive ? tokens.colors.ui.destructive : tokens.colors.surfaces["container-background"],
        boxShadow: tokens.effects.effects.shadow.default,
        border: `${tokens.globals["spacing-px"]} solid ${destructive ? tokens.colors.ui.destructive : tokens.colors.surfaces["border-divider"]}`,
        color: destructive ? tokens.colors.neutral.white : tokens.colors.text.primary,

        "&:hover:not(:disabled)": destructive
            ? { background: tokens.colors.states.alert["600"], borderColor: tokens.colors.states.alert["600"] }
            : { borderColor: tokens.colors.neutral.grey["500"] },

        "&:focus-visible": {
            outline: `${tokens.globals["spacing-px"]} solid ${destructive ? tokens.colors.ui.destructive : tokens.colors.ui.ring}`,
            outlineOffset: tokens.globals["spacing-0-5"],
        },

        "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
    };
});

const StyledButtonGroup = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
});

const StyledExtra = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-sm"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
    },
});

/**
 * Standard AIE sticky form footer for Save/Cancel flows. Pinned to the bottom; render it
 * unconditionally (not only while dirty) and pass an "unsaved changes" indicator via `children`.
 * @param {(event: React.MouseEvent) => void} [props.onSave] Save button click handler.
 * @param {(event: React.MouseEvent) => void} [props.onCancel] Cancel button click handler.
 * @param {boolean} [props.isLoading] Shows a loading label and disables both buttons.
 * @param {React.ReactNode} [props.children] Extra content after the buttons (e.g. unsaved-changes text).
 * @param {"destructive"|string} [props.cancelTone="destructive"] "destructive" renders Cancel in red; any other value renders it neutral.
 * Also: saveDisabled, cancelDisabled, saveText, cancelText, saveIcon, cancelIcon, saveType, className (self-explanatory).
 */
function StickyFooter({
    onSave,
    onCancel,
    isLoading = false,
    saveDisabled = false,
    cancelDisabled = false,
    saveText = "Save",
    cancelText = "Cancel",
    saveIcon = "save",
    cancelIcon = "times",
    saveType = "submit",
    cancelTone = "destructive",
    className,
    children,
    ...props
}) {
    return (
        <StyledFooter className={className} {...props}>
            <StyledButtonGroup>
                <StyledSaveButton
                    type={saveType}
                    onClick={onSave}
                    disabled={isLoading || saveDisabled}
                    aria-busy={isLoading || undefined}
                >
                    {saveIcon && <i className={`fal fa-${saveIcon}`} aria-hidden="true" />}
                    {isLoading ? `${saveText}...` : saveText}
                </StyledSaveButton>

                <StyledCancelButton
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading || cancelDisabled}
                    $tone={cancelTone}
                >
                    {cancelIcon && <i className={`fal fa-${cancelIcon}`} aria-hidden="true" />}
                    {cancelText}
                </StyledCancelButton>
            </StyledButtonGroup>

            {children && <StyledExtra>{children}</StyledExtra>}
        </StyledFooter>
    );
}

StickyFooter.displayName = "StickyFooter";

export default StickyFooter;
